import type {
	Client as TwilioClient,
	Conversation as TwilioConversation,
} from '@twilio/conversations';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	type Content,
	encodeAdf,
	isAdfContent,
} from '@atlassian/conversation-assistant-content-transformer/adf-encoder';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';
import { EMPTY_CONVERSATION_ID } from '@atlassian/conversation-assistant-store/controllers/chat';
import {
	isStoreTwilioLiveChatAgent,
	isStoreTwilioLiveChatHuman,
	isStoreTwilioLiveChatMessage,
	CHAT_STORE_STATUS,
	type CsmTwilioAttachment,
	type StoreChatItem,
	type StoreTwilioLiveChat,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { setDraftMessage } from '@atlassian/conversation-assistant-store/controllers/drafts';

import { conversationIsEnded } from '../../common/utils/conversation-status';
import { extractHelpCenterIdFromAri } from '../../common/utils/extract-help-center-id-from-ari';
import { whatsAppMarkdownToAdf } from '../../common/utils/whatsapp-markdown-to-adf';
import { isWhatsAppAuthor } from '../../extensions/twilio/is-whatsapp-author';
import { AccessTokenRequestError, NoAgentAvailableError } from '../../extensions/twilio/ocg-client';
import {
	getTwilioClientToken,
	removeTwilioClientToken,
	setTwilioClientToken,
} from '../../extensions/twilio/ocg-client-store';
import { TOKEN_ABOUT_TO_EXPIRE_BUFFER_SECONDS } from '../../extensions/twilio/ocg-client-utils';
import { type TokenRefreshResult } from '../../extensions/twilio/ocg-token-refresh';
import type { OcgClient } from '../../extensions/twilio/use-ocg-client';

import type { AI_WIDGET_STORE_UFO_EXPERIENCES } from './experiences';
import type {
	ChatStoreAction,
	OmniEnhancedConversation,
	TwilioConversationsWithMetadata,
	WidgetChatStorePrivateState,
} from './types';
import {
	createStoreHumanForTwilioLiveChat,
	createStoreHumanForTwilioLiveChatWithAttachments,
	genIntId,
} from './utils';

/**
 * Safely look up a UFO experience by name on the optional `ufoExperiences`
 * container prop and start its instance for the given key. Returns the
 * concurrent experience instance (or undefined if unavailable), which the
 * caller is responsible for marking as `success()` / `failure()`.
 */
const startLiveChatUfoExperience = (
	ufoExperiences: typeof AI_WIDGET_STORE_UFO_EXPERIENCES | undefined,
	name: keyof typeof AI_WIDGET_STORE_UFO_EXPERIENCES,
	instanceKey: string,
) => {
	const experience = ufoExperiences?.[name];
	if (!experience) {
		return undefined;
	}
	const instance = experience.getInstance(instanceKey);
	instance.start();
	return instance;
};

export type OcgTwilioClientModule = typeof import('../../extensions/twilio/ocg-twilio-client');

/**
 * Temporary conversation ID used during direct live chat initialization.
 * This placeholder allows optimistic UI rendering before the real conversation ID
 * is returned from the /livechat/direct API endpoint.
 */
const DIRECT_LIVE_CHAT_PENDING_ID = 'DIRECT_LIVE_CHAT_PENDING';

/**
 * Returns true if the conversation ID is the temporary pending ID used during
 * direct live chat initialization.
 */
export const isPendingDirectLiveChat = (conversationId: string | undefined): boolean =>
	conversationId === DIRECT_LIVE_CHAT_PENDING_ID;

/** Dependencies needed by token-refresh and client management helpers. */
type TwilioClientContext = {
	getState: () => WidgetChatStorePrivateState;
	setState: (partial: Partial<WidgetChatStorePrivateState>) => void;
	ocgTwilioClientModule: OcgTwilioClientModule;
	attachTokenRefresh: typeof import('../../extensions/twilio/ocg-token-refresh').attachTokenRefresh;
	ocgClient: OcgClient;
	/**
	 * Help-kosh customer experience ID. Forwarded to the OCG `/access-token`
	 * endpoint as a query parameter so the issued (and refreshed) Twilio tokens
	 * stay scoped to the help center the help-seeker is interacting with.
	 *
	 * Required: callers (`initializeTwilioConversationImpl`,
	 * `initializeDirectLiveChatImpl`) construct this context only after they
	 * have a guaranteed-resolved `helpCenterId`.
	 */
	helpCenterId: string;
	/**
	 * Optional UFO experience map forwarded from `ChatStoreContainerProps`.
	 * Used to start/succeed/fail FE observability events for the help-seeker
	 * Twilio flows (BEARS-1036) without coupling these helpers to the broader
	 * react-sweet-state container props.
	 */
	ufoExperiences?: typeof AI_WIDGET_STORE_UFO_EXPERIENCES;
};

/**
 * Restores conversation objects on a new client by calling getConversationBySid
 * for each conversation. Returns a new map with updated Conversation references.
 * Conversations that fail to restore are excluded from the result.
 *
 * Does NOT call setState — the caller is responsible for an atomic state update.
 */
const restoreConversationsOnClient = async (
	client: TwilioClient,
	conversationsWithMetadata: TwilioConversationsWithMetadata,
	twilioClientContext: TwilioClientContext,
): Promise<TwilioConversationsWithMetadata> => {
	const { ocgTwilioClientModule, ufoExperiences } = twilioClientContext;
	const { getTwilioConversation } = ocgTwilioClientModule;

	const restoredConversations: TwilioConversationsWithMetadata = {};

	// Skip the no-op case entirely. A token refresh that fires when the user
	// has no active conversations would otherwise emit a spurious "success"
	// UFO event for an operation that didn't actually do anything, polluting
	// the dashboards with phantom traffic.
	const hasConversationsToRestore = Object.keys(conversationsWithMetadata).length > 0;

	// FE observability (BEARS-1036): "Continue active chat" event for the
	// token-driven client swap. We deliberately fire ONE bulk experience for
	// the whole batch (not per-conversation) so the UFO timeline reflects the
	// user-visible "rehydrate everything" operation. Keyed off the client SID
	// (or a stable fallback) to avoid colliding with the per-mount
	// `hydrateTwilioConversationImpl` experience.
	const bulkUfoExperience = hasConversationsToRestore
		? startLiveChatUfoExperience(
				ufoExperiences,
				'HYDRATE_TWILIO_LIVE_CHAT',
				`restore:${client.user?.identity ?? 'unknown'}`,
			)
		: undefined;

	const restoreResults = await Promise.allSettled(
		Object.entries(conversationsWithMetadata).map(
			async ([conversationId, conversationWithMetadata]) => {
				const conversation = await getTwilioConversation(
					client,
					conversationWithMetadata.conversationSid,
				);
				restoredConversations[conversationId] = {
					...conversationWithMetadata,
					conversation,
				};
			},
		),
	);

	let anyRestoreFailed = false;
	for (const result of restoreResults) {
		if (result.status === 'rejected') {
			anyRestoreFailed = true;
			logException({
				exception: result.reason,
				name: 'TwilioConversationRestoreFailed',
			});
		}
	}

	// Single success/failure call covering the whole batch: success only if
	// every per-conversation restore resolved, failure if any rejected.
	if (anyRestoreFailed) {
		bulkUfoExperience?.failure();
	} else {
		bulkUfoExperience?.success();
	}

	return restoredConversations;
};

/**
 * Handles a token refresh result by updating the store and localStorage.
 * When the token fully expired (newClient is set), the old client's token-refresh
 * listeners are detached, the new client replaces it, all active conversations
 * are restored, and fresh token-refresh listeners are attached to the new client.
 */
const handleTokenRefreshResult = async (
	refreshResult: TokenRefreshResult,
	twilioClientContext: TwilioClientContext,
): Promise<void> => {
	const { meta: tokenMeta, client: newClient } = refreshResult;
	const { getState, setState } = twilioClientContext;
	const currentState = getState();

	// Update client token in localStorage (single write — token is user-level)
	setTwilioClientToken(tokenMeta);

	if (newClient) {
		// Token fully expired — client was replaced.
		// Clean up old refresh listener
		currentState.twilioTokenRefreshCleanup?.();

		// Restore all active conversations on the new client.
		const restoredConversations = await restoreConversationsOnClient(
			newClient,
			currentState.twilioConversationsWithMetadata,
			twilioClientContext,
		);

		// Re-read state after await — conversations may have been added or removed
		// while the restore was in flight.
		const latestConversations = getState().twilioConversationsWithMetadata;

		// Build the merged conversation map:
		// - Conversations that existed before AND restored successfully → use restored (new client ref)
		// - Conversations added during the await → keep as-is (already on the new client)
		// - Conversations that failed to restore → remove (stale old-client references)
		// - Conversations the user ended during the await → don't re-add
		const mergedConversations: typeof latestConversations = {};
		for (const [id, latest] of Object.entries(latestConversations)) {
			if (id in restoredConversations) {
				// Successfully restored on new client
				mergedConversations[id] = restoredConversations[id];
			} else if (!(id in currentState.twilioConversationsWithMetadata)) {
				// Added during the await — already on the new client
				mergedConversations[id] = latest;
			}
		}

		setState({
			twilioClient: newClient,
			twilioClientTokenMeta: tokenMeta,
			twilioTokenRefreshCleanup: null,
			twilioConversationsWithMetadata: mergedConversations,
		});

		// Attach token refresh to the new client
		attachTokenRefreshToClient(newClient, twilioClientContext);
	} else {
		// Token refreshed in place — just update the shared token metadata
		setState({ twilioClientTokenMeta: tokenMeta });
	}
};

/**
 * Attaches a token-refresh listener to the single shared Twilio client,
 * cleaning up any previous listener first.
 */
const attachTokenRefreshToClient = (
	client: TwilioClient,
	twilioClientContext: TwilioClientContext,
): void => {
	const { getState, setState, attachTokenRefresh, ocgClient, helpCenterId } = twilioClientContext;

	// Clean up any existing listener
	getState().twilioTokenRefreshCleanup?.();

	const cleanup = attachTokenRefresh(
		client,
		(refreshResult) => {
			handleTokenRefreshResult(refreshResult, twilioClientContext).catch((err) => {
				logException({
					exception: err,
					name: 'TwilioTokenRefreshHandlingFailed',
				});
			});
		},
		helpCenterId,
		ocgClient,
	);
	setState({ twilioTokenRefreshCleanup: cleanup });
};

/**
 * In-flight client creation promise. Used by {@link getOrCreateTwilioClient}
 * to deduplicate concurrent calls — all callers receive the same client.
 */
let twilioClientPromise: Promise<TwilioClient> | null = null;

/**
 * Populates `twilioClientTokenMeta` in the store without creating a Twilio
 * SDK client. Used when the help-seeker only needs the identity (e.g. to
 * attribute backfilled messages from the BE endpoint) but doesn't need an
 * active SDK connection — for example when restoring an omni-processed (and
 * thus deleted) Twilio conversation.
 *
 * Prefers the localStorage-cached token (zero network cost) since we only
 * need the `identity` field, which is stable across token refreshes. Falls
 * back to a fresh token fetch only when no cached token is available.
 */
const populateTwilioClientTokenMeta = async (
	twilioClientContext: TwilioClientContext,
): Promise<void> => {
	const { getState, setState, ocgClient, helpCenterId } = twilioClientContext;
	if (getState().twilioClientTokenMeta) {
		return;
	}

	// Prefer the localStorage-cached token — we only need `identity`, which is
	// stable and doesn't require a valid (non-expired) token.
	const storedToken = getTwilioClientToken();
	if (storedToken) {
		setState({ twilioClientTokenMeta: storedToken });
		return;
	}

	// No cached token — fetch a fresh one. Forward the `helpCenterId` so the
	// OCG `/access-token` endpoint can scope the issued token to the help
	// center the help-seeker is interacting with (BEARS-1780). Auth header
	// resolution + 401 retry are centralised inside `ocgClient` (BEARS-1804).
	const tokenMeta = await ocgClient.fetchTokenAndBuildMeta(helpCenterId);
	// Re-check after the await in case another call populated the meta concurrently.
	if (!getState().twilioClientTokenMeta) {
		setState({ twilioClientTokenMeta: tokenMeta });
		setTwilioClientToken(tokenMeta);
	}
};

/**
 * Gets the existing shared Twilio client, or creates a new one if none exists.
 * When creating a new client, also attaches the token refresh listener.
 *
 * On first call (e.g. page restore), tries to reuse a token previously persisted
 * to localStorage. If the stored token has enough remaining lifetime (more than
 * {@link TOKEN_ABOUT_TO_EXPIRE_BUFFER_SECONDS}), it is reused to avoid an
 * unnecessary network request. Otherwise a fresh token is fetched.
 *
 * Concurrent calls are safe — if a creation is already in flight, subsequent
 * callers will receive the same promise (and thus the same client).
 */
const getOrCreateTwilioClient = (
	twilioClientContext: TwilioClientContext,
): Promise<TwilioClient> => {
	const { getState, setState } = twilioClientContext;
	const existingClient = getState().twilioClient;
	if (existingClient) {
		return Promise.resolve(existingClient);
	}

	// BEARS-1934: if a previous bootstrap attempt failed with a non-2xx HTTP
	// response from `/access-token`, treat the failure as terminal and stop
	// retrying. Without this guard, every re-render that calls into
	// `getOrCreateTwilioClient` (via the `TwilioLiveChatManager` effects or
	// `hydrateTwilioConversation`) would fire another POST, producing the
	// observed endless-loop behaviour against `/access-token`.
	const fatalError = getState().twilioBootstrapFatalError;
	if (fatalError) {
		return Promise.reject(new AccessTokenRequestError(fatalError.message, fatalError.status));
	}

	if (twilioClientPromise) {
		return twilioClientPromise;
	}

	twilioClientPromise = (async () => {
		const { ocgTwilioClientModule, ocgClient, helpCenterId } = twilioClientContext;
		const { createTwilioClient } = ocgTwilioClientModule;

		// Try reusing a stored token that has enough remaining lifetime.
		const storedToken = getTwilioClientToken();
		const now = Date.now() / 1000;
		const isStoredTokenUsable =
			storedToken != null && storedToken.expiration > now + TOKEN_ABOUT_TO_EXPIRE_BUFFER_SECONDS;

		const tokenMeta = isStoredTokenUsable
			? storedToken
			: await ocgClient.fetchTokenAndBuildMeta(helpCenterId);

		const client = createTwilioClient(tokenMeta.token);

		setState({ twilioClient: client, twilioClientTokenMeta: tokenMeta });
		setTwilioClientToken(tokenMeta);
		attachTokenRefreshToClient(client, twilioClientContext);

		return client;
	})()
		.catch((err) => {
			// BEARS-1934: any non-2xx HTTP response from `/access-token` is
			// treated as terminal — record a sticky `twilioBootstrapFatalError`
			// on the store so subsequent calls (and `useEffect`s in
			// `TwilioLiveChatManager`) short-circuit instead of re-issuing
			// another POST. This covers both 4xx (auth/permission/contract
			// errors) and 5xx (gateway / upstream errors): in either case the
			// user must reload to retry.
			if (err instanceof AccessTokenRequestError) {
				setState({
					twilioBootstrapFatalError: { status: err.status, message: err.message },
				});
			}
			throw err;
		})
		.finally(() => {
			twilioClientPromise = null;
		});

	return twilioClientPromise;
};

// ---------------------------------------------------------------------------
// Store action implementations
// ---------------------------------------------------------------------------

type InitializeLiveChatAction = (payload: { remoteEntityId: string } | null) => ChatStoreAction;

type InitializeTwilioConversationParams = {
	conversationId?: string;
	ocgTwilioClientModule?: OcgTwilioClientModule;
	initializeLiveChat: InitializeLiveChatAction;
};

export type InitializeDirectLiveChatParams = {
	helpCenterId: string;
	metadata?: Record<string, string>;
	/** Optional initial message (ADF DocNode) to render optimistically and send via Twilio. */
	initialMessage?: DocNode;
	/** Localized display name for the conversation (shown in chat history). */
	conversationName: string;
};

/**
 * Renders the user's message optimistically using a temporary pending conversation ID.
 * This gives instant UI feedback while the slow /livechat/direct API call (~8s) is in flight.
 */
const renderOptimisticPendingMessage = (
	getState: () => WidgetChatStorePrivateState,
	setState: (partial: Partial<WidgetChatStorePrivateState>) => void,
	conversationName: string,
	initialMessage: DocNode,
): StoreChatItem => {
	const pendingMessage = createStoreHumanForTwilioLiveChat(genIntId(), initialMessage);

	setState({
		conversations: {
			...getState().conversations,
			[DIRECT_LIVE_CHAT_PENDING_ID]: {
				id: DIRECT_LIVE_CHAT_PENDING_ID,
				name: conversationName,
				messages: [pendingMessage],
				status: CHAT_STORE_STATUS.idle,
				traces: [],
				timeCreated: new Date().toISOString(),
			},
		},
		currentConversationId: DIRECT_LIVE_CHAT_PENDING_ID,
	});

	return pendingMessage;
};

/**
 * Transfers optimistically-rendered messages from the temporary pending conversation
 * to the real conversation, and updates the store with the final conversation state.
 */
const transferPendingToRealConversation = (
	getState: () => WidgetChatStorePrivateState,
	setState: (partial: Partial<WidgetChatStorePrivateState>) => void,
	{
		conversationId,
		conversationName,
		createdAt,
		liveChatConversationId,
		interactionId,
		twilioConversation,
		pendingMessage,
	}: {
		conversationId: string;
		conversationName: string;
		createdAt: string;
		liveChatConversationId: string;
		interactionId: string;
		twilioConversation: Awaited<ReturnType<OcgTwilioClientModule['getTwilioConversation']>>;
		pendingMessage: StoreChatItem | null;
	},
): void => {
	const currentConversations = { ...getState().conversations };
	const messagesFromTemp = pendingMessage ? [pendingMessage] : [];
	delete currentConversations[DIRECT_LIVE_CHAT_PENDING_ID];

	const directLiveChatConversation: OmniEnhancedConversation = {
		id: conversationId,
		name: conversationName,
		messages: messagesFromTemp,
		status: CHAT_STORE_STATUS.idle,
		traces: [],
		convoAIConversationId: null,
		twilioConversationId: liveChatConversationId,
		interactionId,
		timeCreated: createdAt,
		lastMessageTimestamp: createdAt,
	};

	setState({
		conversations: {
			...currentConversations,
			[conversationId]: directLiveChatConversation,
		},
		currentConversationId: conversationId,
		twilioConversationsWithMetadata: {
			...getState().twilioConversationsWithMetadata,
			[conversationId]: {
				conversation: twilioConversation,
				conversationSid: liveChatConversationId,
				unreadMessagesCount: 0,
				isUnreadMessagesCountSet: false,
			},
		},
		isInitializingTwilioConversation: false,
		liveChatMetadata: { remoteEntityId: liveChatConversationId },
	});
};

/**
 * Removes the temporary pending conversation and resets state on initialization failure.
 * Without this cleanup, DIRECT_LIVE_CHAT_PENDING_ID would remain in the store
 * as an orphaned conversation with the user's message in a dead-end.
 */
const cleanupPendingConversation = (
	getState: () => WidgetChatStorePrivateState,
	setState: (partial: Partial<WidgetChatStorePrivateState>) => void,
): void => {
	const currentConversations = { ...getState().conversations };
	delete currentConversations[DIRECT_LIVE_CHAT_PENDING_ID];
	setState({
		conversations: currentConversations,
		currentConversationId: EMPTY_CONVERSATION_ID,
		isInitializingTwilioConversation: false,
		directLiveChatNoAgentAvailable: false,
	});
};

/**
 * Creates a direct live chat conversation (no AI involvement).
 * Calls POST /conversation/livechat then joins the Twilio conversation.
 * The conversation is keyed by the Twilio SID (CH-prefixed, no collision with convoAI UUIDs).
 */
export const initializeDirectLiveChatImpl =
	({
		helpCenterId,
		metadata,
		initialMessage,
		conversationName,
	}: InitializeDirectLiveChatParams): ChatStoreAction =>
	async ({ getState, setState }, containerProps) => {
		const { ocgClient, ufoExperiences } = containerProps;
		if (!initialMessage) {
			return;
		}

		setState({
			isInitializingTwilioConversation: true,
			directLiveChatNoAgentAvailable: false,
		});

		const pendingMessage = renderOptimisticPendingMessage(
			getState,
			setState,
			conversationName,
			initialMessage,
		);

		const ocgTwilioClientModule = await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-widget/ocg-twilio-client" */ '../../extensions/twilio/ocg-twilio-client'
		);

		const { getTwilioConversation } = ocgTwilioClientModule;

		const { attachTokenRefresh } = await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-widget/ocg-token-refresh" */ '../../extensions/twilio/ocg-token-refresh'
		);

		const twilioClientContext: TwilioClientContext = {
			getState,
			setState,
			ocgTwilioClientModule,
			attachTokenRefresh,
			ocgClient,
			helpCenterId,
			ufoExperiences,
		};

		// FE observability (BEARS-1036): "Start a new live chat" via direct chat
		// (CSM hand-off path). Keyed off `DIRECT_LIVE_CHAT_PENDING_ID` because
		// the real conversation ID isn't known until the BE call returns.
		const ufoExperience = startLiveChatUfoExperience(
			ufoExperiences,
			'INITIALIZE_TWILIO_LIVE_CHAT',
			DIRECT_LIVE_CHAT_PENDING_ID,
		);

		try {
			// 1. Get or create the shared Twilio client
			const client = await getOrCreateTwilioClient(twilioClientContext);

			// 2. Create the live chat conversation via API. Auth header lookup +
			// 401 retry are centralised inside `ocgClient` (BEARS-1804). The intake
			// channel is set on containerProps by the entry point (embed.tsx or
			// main.tsx) and forwarded to Barista as-is.
			const { interactionId, liveChatConversationId, name, createdAt } =
				await ocgClient.createDirectLiveChatConversation(
					helpCenterId,
					initialMessage,
					metadata,
					containerProps.interactionChannel,
				);

			// 3. Join the Twilio conversation
			const twilioConversation = await getTwilioConversation(client, liveChatConversationId);
			// FE observability (BEARS-1036): mark the success of "Start a new live
			// chat" once `getTwilioConversation` resolves successfully.
			ufoExperience?.success();

			// 4. Transfer pending message to real conversation and update store
			transferPendingToRealConversation(getState, setState, {
				conversationId: liveChatConversationId,
				conversationName: name,
				createdAt,
				liveChatConversationId,
				interactionId,
				twilioConversation,
				pendingMessage,
			});

			// Note: No separate persistence needed — the twilioConversationId is already
			// set on the conversation object via transferPendingToRealConversation above,
			// and the omni-core list endpoint provides it on page refresh.
		} catch (error) {
			// FE observability (BEARS-1036): mark the experience as failed for any
			// error that prevents the help-seeker from joining the Twilio
			// conversation. NoAgentAvailableError is treated the same way because
			// the user never enters the live chat.
			ufoExperience?.failure();
			if (error instanceof NoAgentAvailableError) {
				// No agent available — keep the pending conversation with the user's
				// message visible, stop the loading state, and surface a user-friendly
				// error system message via the store flag. The UI reads
				// `directLiveChatNoAgentAvailable` to render a
				// TwilioLiveChatTransientSystemMessage with variant="NO_AGENT_AVAILABLE".
				//
				// Because the BE never persisted this conversation, it would be lost
				// on a page refresh. To avoid the user losing the message they just
				// typed, seed the EMPTY_CONVERSATION_ID draft with the same ADF so
				// that when the user clicks "New chat" the message is restored in
				// the input. The pending conversation itself is hidden from chat
				// history (see chat-history-list filtering) and is otherwise left
				// in place until the next page reload.
				setDraftMessage(EMPTY_CONVERSATION_ID, initialMessage);
				setState({
					isInitializingTwilioConversation: false,
					directLiveChatNoAgentAvailable: true,
				});
				return;
			}
			cleanupPendingConversation(getState, setState);
			throw error;
		}
	};

/**
 * Handle the case where a Twilio conversation lookup returned "Not Found"
 * (i.e. the Twilio resource is gone server-side — deleted, expired, or
 * otherwise no longer available). Marks the conversation as ENDED locally
 * and clears its stale `twilioConversationId`.
 *
 * Marking the conversation ENDED:
 *   - causes `useIsChatConsideredEndedInWidget` to return true, which renders
 *     the EndedFooter (with the "New chat" button) and a sensible empty state
 *     instead of leaving the conversation looking "active" with only the AI
 *     greeting and no way for the user to start a new chat.
 *   - prevents the LazyRestore useEffect in TwilioLiveChatManager from
 *     retrying endlessly (the effect skips conversations whose stored sid
 *     becomes null, and downstream effects skip ended conversations).
 *
 * Safe to call when the conversation is no longer in the store (no-ops the
 * state update). No Sentry: this is an expected outcome (Twilio resource
 * has been cleaned up server-side). Reliability is tracked by the
 * `HYDRATE_TWILIO_LIVE_CHAT` UFO experience, whose `.failure()` is marked
 * by the callers in their surrounding `catch` block (including the `Not
 * Found` branch) so spike detection still works.
 */
const markTwilioConversationEndedWhenNotFound = ({
	conversationId,
	getState,
	setState,
}: {
	conversationId: string;
	getState: () => WidgetChatStorePrivateState;
	setState: (state: Partial<WidgetChatStorePrivateState>) => void;
}): void => {
	const currentConversation = getState().conversations[conversationId];
	if (currentConversation) {
		setState({
			conversations: {
				...getState().conversations,
				[conversationId]: {
					...currentConversation,
					twilioConversationId: null,
					channelStatus: 'ENDED',
				} as OmniEnhancedConversation,
			},
		});
	}
};

export const initializeTwilioConversationImpl =
	({
		conversationId,
		ocgTwilioClientModule,
		initializeLiveChat,
	}: InitializeTwilioConversationParams): ChatStoreAction =>
	async ({ dispatch, getState, setState }, containerProps) => {
		const { ocgClient, ufoExperiences } = containerProps;
		// Derive the help-kosh customer experience ID from the help-center ARI on
		// `containerProps`. It is required by every downstream layer; if it cannot
		// be resolved (no `customerHubId`, or the ARI doesn't contain a
		// help-center segment) we throw at the contract boundary so the failure
		// is surfaced via `containerProps.setGlobalError` through the standard
		// `createSafeAction` error path (BEARS-1780).
		const helpCenterId = containerProps.customerHubId
			? extractHelpCenterIdFromAri(containerProps.customerHubId)
			: null;
		if (!helpCenterId) {
			throw new Error('helpCenterId must be provided for Twilio');
		}
		const state = getState();

		if (
			!conversationId ||
			conversationId === EMPTY_CONVERSATION_ID ||
			!state.conversations[conversationId]
		) {
			dispatch(initializeLiveChat(null));
			// avoid starting a Twilio Live Chat conversation without a non-empty convo-ai conversation
			return;
		}

		if (!ocgTwilioClientModule) {
			return;
		}

		setState({ isInitializingTwilioConversation: true });

		const { getTwilioConversation } = ocgTwilioClientModule;

		const { attachTokenRefresh } = await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-widget/ocg-token-refresh" */ '../../extensions/twilio/ocg-token-refresh'
		);

		const twilioClientContext: TwilioClientContext = {
			getState,
			setState,
			ocgTwilioClientModule,
			attachTokenRefresh,
			ocgClient,
			helpCenterId,
			ufoExperiences,
		};

		// FE observability (BEARS-1036): "Start a new live chat" success event for
		// the JSM hand-off path (an existing AI conversation is being upgraded to
		// a Twilio live chat). Started conditionally so we never report a "no-op"
		// experience when there is no Twilio conversation to look up.
		let ufoExperience: ReturnType<typeof startLiveChatUfoExperience>;

		try {
			const existingConversation = state.conversations[conversationId] as
				| OmniEnhancedConversation
				| undefined;
			const existingConversationSid = existingConversation?.twilioConversationId ?? null;
			const omniProcessed = existingConversation?.omniProcessed ?? false;

			// When the Twilio conversation has been processed (and deleted) by
			// omni-core, calling getTwilioConversation will always 404. Skip the
			// SDK lookup entirely — the help-seeker side will backfill historical
			// messages via the BE endpoint in TwilioLiveChatManager (BEARS-1713).
			// We still need the help-seeker's Twilio identity to correctly
			// attribute backfilled messages (right- vs left-aligned), so ensure
			// twilioClientTokenMeta is populated via a lightweight token fetch
			// (no SDK client creation needed).
			if (omniProcessed) {
				await populateTwilioClientTokenMeta(twilioClientContext);
				setState({ isInitializingTwilioConversation: false });
				return;
			}

			if (existingConversationSid) {
				ufoExperience = startLiveChatUfoExperience(
					ufoExperiences,
					'INITIALIZE_TWILIO_LIVE_CHAT',
					conversationId,
				);
				const client = await getOrCreateTwilioClient(twilioClientContext);
				const conversation = await getTwilioConversation(client, existingConversationSid);
				// FE observability (BEARS-1036): success once the existing Twilio
				// conversation has been resolved successfully.
				ufoExperience?.success();

				const isEnded = conversationIsEnded(state.conversations[conversationId]);
				const isTwilioConversationClosed = conversation.state?.current === 'closed';

				// Only activate live chat UI for conversations that are still active.
				// Check both our store state (channelStatus === 'ENDED') and the Twilio
				// SDK's conversation state to avoid restoring stale live chat state
				// (e.g. liveChatMetadata, csmLiveChatSubscriptionId, transient system
				// message) after the agent resolves the chat and the user refreshes.
				// Note: we intentionally do NOT check for 'inactive' here — an inactive
				// Twilio conversation is simply one that has been idle, not one that has
				// been resolved. It can still receive messages and transition back to
				// 'active', so the help-seeker should still see the live chat UI.
				if (!isEnded && !isTwilioConversationClosed) {
					dispatch(initializeLiveChat({ remoteEntityId: existingConversationSid }));
				}
				setState({
					twilioConversationsWithMetadata: {
						...getState().twilioConversationsWithMetadata,
						[conversationId]: {
							conversation,
							conversationSid: existingConversationSid,
							unreadMessagesCount: 0,
							isUnreadMessagesCountSet: false,
						},
					},
					isInitializingTwilioConversation: false,
				});

				return;
			}

			// No existing Twilio conversation found for this convo-ai conversation.
			// Server-initiated handoff will create the Twilio conversation when an
			// agent accepts the escalation — nothing to do on the client side.
			setState({ isInitializingTwilioConversation: false });
		} catch (error) {
			// FE observability (BEARS-1036): mark failure when getTwilioConversation
			// throws. `ufoExperience` may be undefined (no existing SID, etc.),
			// in which case the optional-chain is a no-op.
			ufoExperience?.failure();
			const errorMessage = error instanceof Error ? error.message : String(error);

			if (conversationId && errorMessage === 'Not Found') {
				markTwilioConversationEndedWhenNotFound({ conversationId, getState, setState });
				setState({ isInitializingTwilioConversation: false });
				// Don't re-throw — the conversation is simply gone on Twilio's side.
				// This is expected for ended conversations whose Twilio resources were cleaned up.
				return;
			}

			// Reset loading state on error
			setState({
				isInitializingTwilioConversation: false,
			});
			throw error;
		}
	};

export const cleanupTwilioTokenRefreshImpl =
	(): ChatStoreAction =>
	({ getState, setState }) => {
		getState().twilioTokenRefreshCleanup?.();
		setState({ twilioTokenRefreshCleanup: null });
	};

/**
 * Cleans up Twilio resources for a specific conversation:
 * - Removes the conversation from in-memory state
 * - Preserves the conversation SID in localStorage so that historical messages
 *   can be backfilled when the user revisits an ended conversation
 * - If no more active conversations remain, shuts down the shared Twilio client
 *   and removes the token refresh listener
 */
export const cleanupTwilioConversationImpl =
	(conversationId: string): ChatStoreAction =>
	async ({ getState, setState }) => {
		const currentState = getState();

		// Clear typing indicators and agent joined state, but preserve conversation metadata
		// so that the CSAT survey and "conversation ended" message can still render
		const { [conversationId]: _removedConversation, ...remainingAgentJoinedConversations } =
			currentState.twilioConversationAgentJoined;
		setState({
			twilioAgentsTyping: [],
			twilioConversationAgentJoined: remainingAgentJoinedConversations,
		});

		// If no more active conversations remain, shut down the shared client
		if (
			Object.keys(currentState.twilioConversationsWithMetadata).length === 0 &&
			currentState.twilioClient
		) {
			currentState.twilioTokenRefreshCleanup?.();
			currentState.twilioClient.shutdown().catch((err) => {
				logException({
					exception: err,
					name: 'TwilioClientShutdownFailed',
				});
			});
			removeTwilioClientToken();
			setState({
				twilioClient: null,
				twilioClientTokenMeta: null,
				twilioTokenRefreshCleanup: null,
			});
		}
	};

/**
 * Shuts down the shared Twilio client and clears in-memory conversation state.
 * Call on widget unmount together with {@link cleanupTwilioTokenRefreshImpl} so that when the user
 * returns we restore from localStorage and re-attach token refresh listeners.
 *
 * Intentionally does NOT remove localStorage data (conversation SIDs and client token)
 * — that data is needed to restore active conversations when the user navigates back.
 */
export const clearTwilioConversationsWithMetadataImpl =
	(): ChatStoreAction =>
	({ getState, setState }) => {
		const client = getState().twilioClient;
		if (client) {
			client.shutdown().catch((err) => {
				logException({
					exception: err,
					name: 'TwilioClientShutdownFailed',
				});
			});
		}
		setState({
			twilioClient: null,
			twilioClientTokenMeta: null,
			twilioConversationsWithMetadata: {},
			twilioConversationAgentJoined: {},
		});
	};

/** Attributes attached to Twilio system messages by the backend. */
export type TwilioMessageAttributes = {
	event?: string;
	twilioUsername?: string;
	/**
	 * Help-seeker–generated UUID set when sending a Twilio message via the
	 * widget. Used by the `messageAdded` listener to dedupe a help-seeker's
	 * own message echo against any optimistic store entry. CSM-only.
	 */
	csmClientMessageId?: string;
	/**
	 * Original Atlassian ADF for the message body, stashed by the agent view so
	 * the widget renders exact formatting instead of re-parsing the lossy
	 * WhatsApp markdown delivered in `body`.
	 */
	adfbody?: DocNode;
};

/** Twilio message data without store-specific fields. */
export type TwilioLiveChatMessage = {
	sid: string;
	content: string;
	author: string | null;
	dateCreated: Date | null;
	conversationSid: string;
	attributes?: TwilioMessageAttributes;
	/**
	 * Native Twilio Media attachments on this Message, mapped from
	 * `Message.attachedMedia`. Populated by both the realtime SDK path
	 * and the historical OCG endpoint path so attachments survive page
	 * refreshes. Only populated when the
	 * `csm-omni-channel-live-chat-attachments` feature gate is on.
	 */
	attachments?: CsmTwilioAttachment[];
};

export type AddTwilioLiveChatMessageParams = TwilioLiveChatMessage & { conversationId?: string };

export type BackfillLiveChatMessagesParams = {
	messagesToBackfill: TwilioLiveChatMessage[];
	helpSeekerIdentity: string | null;
	conversationId?: string;
};

/**
 * Whether a live chat message should be attributed to the help-seeker
 * (rendered right-aligned with no agent avatar/name) rather than an agent.
 * True for the widget's own Twilio identity, or for a WhatsApp help-seeker
 * (`whatsapp:<E.164>`) behind `csm-omni-whatsapp-support`.
 */
const isHelpSeekerAuthor = (author: string | null, helpSeekerIdentity: string | null): boolean => {
	if (!!helpSeekerIdentity && author === helpSeekerIdentity) {
		return true;
	}
	return isWhatsAppAuthor(author) && fg('csm-omni-whatsapp-support');
};

// Pre-`csm-omni-whatsapp-support` behaviour: throws on a non-JSON body so
// callers drop the message. Kept as the gate-off fallback.
const liveChatBodyToAdfDocLegacy = (content: string | null | undefined): DocNode => {
	const raw = content && content.length > 0 ? content : '';
	if (raw.length === 0) {
		return { type: 'doc', version: 1, content: [] };
	}
	return JSON.parse(raw) as DocNode;
};

// WhatsApp bodies need decoding before ADF conversion; `encodeAdf`'s CommonMark
// parser alone would mis-read the WhatsApp dialect.
const markdownToAdfDoc = (raw: string): DocNode =>
	fg('csm-omni-whatsapp-support') ? whatsAppMarkdownToAdf(raw) : encodeAdf(raw);

// Widget-sent bodies are ADF JSON; external-channel bodies (e.g. WhatsApp)
// are markdown text. Use JSON only when it is genuine ADF, otherwise encode
// as markdown. Never throws, so no message is dropped.
const liveChatBodyToAdfDoc = (content: string | null | undefined): DocNode => {
	const raw = content && content.length > 0 ? content : '';
	if (raw.length === 0) {
		return { type: 'doc', version: 1, content: [] };
	}
	try {
		const parsed: Content = JSON.parse(raw);
		return isAdfContent(parsed) ? parsed : markdownToAdfDoc(raw);
	} catch {
		return markdownToAdfDoc(raw);
	}
};

/**
 * Resolves the body to render for a live-chat message, preferring the exact ADF
 * under `attributes.adfbody` (serialised to JSON so it flows through the existing
 * `isAdfContent` pipeline) over the lossy WhatsApp markdown `content`. Messages
 * without `adfbody` keep the current behaviour.
 *
 * Gated behind `csm-omni-whatsapp-support`: `adfbody` is only ever written by
 * the agent view on WhatsApp conversations, so outside that flag there is
 * nothing to prefer and we keep the raw content untouched.
 */
const resolveLiveChatContent = (
	content: string,
	attributes: TwilioMessageAttributes | undefined,
): string => {
	if (!fg('csm-omni-whatsapp-support')) {
		return content;
	}
	const adfbody = attributes?.adfbody;
	return adfbody && isAdfContent(adfbody) ? JSON.stringify(adfbody) : content;
};

const createStoreTwilioLiveChat = (msg: TwilioLiveChatMessage): StoreTwilioLiveChat => ({
	type: 'TWILIO_LIVE_CHAT_AGENT',
	sid: msg.sid,
	content: resolveLiveChatContent(msg.content, msg.attributes),
	author: msg.author,
	dateCreated: msg.dateCreated,
	conversationSid: msg.conversationSid,
	attributes: msg.attributes,
	...(msg.attachments && msg.attachments.length > 0
		? { csmTwilioAttachments: msg.attachments }
		: {}),
});

export const addTwilioLiveChatMessageImpl =
	({
		sid,
		content,
		author,
		dateCreated,
		conversationSid,
		attributes,
		attachments,
		conversationId: conversationIdParam,
	}: AddTwilioLiveChatMessageParams): ChatStoreAction =>
	({ getState, setState }) => {
		const conversationId = conversationIdParam ?? getState().currentConversationId;
		const conversations = getState().conversations;
		const conversation = conversations[conversationId];
		if (!conversation) {
			return;
		}

		setState({
			conversations: {
				...conversations,
				[conversationId]: {
					...conversation,
					messages: [
						...conversation.messages,
						createStoreTwilioLiveChat({
							sid,
							content,
							author,
							dateCreated,
							conversationSid,
							attributes,
							attachments,
						}),
					],
				},
			},
		});
	};

/**
 * Appends a single help-seeker message as a {@link StoreHuman} entry
 * (source=TWILIO_LIVE_CHAT) to the conversation store.  Used by the
 * `messageAdded` real-time path when the author is the help-seeker so
 * the message renders on the right-hand side without an agent avatar.
 *
 * This is intentionally lightweight — it does NO deduplication (unlike
 * backfillLiveChatMessagesImpl).  The caller is responsible for ensuring
 * the message is genuinely new.
 */
export const addTwilioLiveChatHumanMessageImpl =
	({
		sid,
		content,
		author,
		conversationSid,
		attributes,
		attachments,
		conversationId: conversationIdParam,
	}: AddTwilioLiveChatMessageParams): ChatStoreAction =>
	({ getState, setState }) => {
		const conversationId = conversationIdParam ?? getState().currentConversationId;
		const conversations = getState().conversations;
		const conversation = conversations[conversationId];
		if (!conversation) {
			return;
		}
		try {
			// liveChatBodyToAdfDoc handles both ADF-JSON and markdown bodies, and
			// resolveLiveChatContent prefers adfbody over the WhatsApp body.
			const adfContent = fg('csm-omni-whatsapp-support')
				? liveChatBodyToAdfDoc(resolveLiveChatContent(content, attributes))
				: liveChatBodyToAdfDocLegacy(content);
			const hasAttachments = !!attachments && attachments.length > 0;
			const storeHuman = hasAttachments
				? createStoreHumanForTwilioLiveChatWithAttachments([sid, adfContent], attachments)
				: createStoreHumanForTwilioLiveChat(sid, adfContent);
			setState({
				conversations: {
					...conversations,
					[conversationId]: {
						...conversation,
						messages: [...conversation.messages, storeHuman],
					},
				},
			});
		} catch {
			// If ADF parsing fails, fall back to the agent path so the message
			// is at least visible rather than silently dropped. Use the
			// original Twilio `author` (the help-seeker identity) — NOT the
			// message sid — so any avatar / identity lookup keyed on author
			// continues to work, and so the fallback bubble does not get a
			// distinct synthetic identity per message.
			setState({
				conversations: {
					...conversations,
					[conversationId]: {
						...conversation,
						messages: [
							...conversation.messages,
							createStoreTwilioLiveChat({
								sid,
								content,
								author,
								dateCreated: new Date(),
								conversationSid,
								attributes,
								attachments,
							}),
						],
					},
				},
			});
		}
	};

/**
 * Backfills historical live chat messages into the conversation store.
 * Deduplicates against existing messages by checking both TWILIO_LIVE_CHAT_AGENT message SIDs
 * and StoreHuman messages with source='TWILIO_LIVE_CHAT'.
 * Help-seeker messages are added as StoreHuman items; agent messages as StoreTwilioLiveChat items.
 */
export const backfillLiveChatMessagesImpl =
	({
		messagesToBackfill,
		helpSeekerIdentity,
		conversationId: conversationIdParam,
	}: BackfillLiveChatMessagesParams): ChatStoreAction =>
	({ getState, setState }) => {
		const conversationId = conversationIdParam ?? getState().currentConversationId;
		const conversations = getState().conversations;
		const currentConversation = conversations[conversationId];
		if (!currentConversation || messagesToBackfill.length === 0) {
			return;
		}

		// Collect existing message SIDs for deduplication
		const existingStoreChatItems = currentConversation.messages;
		const existingAgentMessageSids = new Set(
			existingStoreChatItems.filter(isStoreTwilioLiveChatAgent).map((m) => m.sid),
		);
		// Help-seeker messages use a different ID (prompt ID) than the Twilio SID,
		// so we can't dedup by SID. Instead, check if any help-seeker live chat
		// messages exist in the store — if so, they were added by sendMessage and
		// should not be backfilled again.
		const hasExistingHelpSeekerMessages = existingStoreChatItems.some(isStoreTwilioLiveChatHuman);

		const unseenMessages = messagesToBackfill.filter((m) => {
			const isHelpSeekerMessage = isHelpSeekerAuthor(m.author, helpSeekerIdentity);
			if (isHelpSeekerMessage) {
				// Skip help-seeker messages if they're already in the store from sendMessage.
				// On page refresh, the store is empty so they need to be backfilled.
				return !hasExistingHelpSeekerMessages;
			}
			return !existingAgentMessageSids.has(m.sid);
		});
		if (unseenMessages.length === 0) {
			return;
		}

		const backfilledStoreChatItems: StoreChatItem[] = [];
		const pushHumanItem = (msg: TwilioLiveChatMessage, adfContent: DocNode) => {
			const hasAttachments = !!msg.attachments && msg.attachments.length > 0;
			backfilledStoreChatItems.push(
				hasAttachments
					? createStoreHumanForTwilioLiveChatWithAttachments([msg.sid, adfContent], msg.attachments)
					: createStoreHumanForTwilioLiveChat(msg.sid, adfContent),
			);
		};

		for (const msg of unseenMessages) {
			if (!isHelpSeekerAuthor(msg.author, helpSeekerIdentity)) {
				backfilledStoreChatItems.push(createStoreTwilioLiveChat(msg));
				continue;
			}

			if (fg('csm-omni-whatsapp-support')) {
				// liveChatBodyToAdfDoc handles both ADF-JSON and markdown bodies
				// and never throws; resolveLiveChatContent prefers adfbody.
				pushHumanItem(
					msg,
					liveChatBodyToAdfDoc(resolveLiveChatContent(msg.content, msg.attributes)),
				);
				continue;
			}

			// Gate off: JSON-only parse, dropping on parse error.
			try {
				pushHumanItem(msg, liveChatBodyToAdfDocLegacy(msg.content));
			} catch (error) {
				logException({
					exception: error,
					name: 'TwilioBackfillAdfParseFailed',
				});
			}
		}

		// fetchLiveChatMessageHistory returns messages sorted by dateCreated, so
		// backfilledStoreChatItems is already correctly interleaved (help-seeker
		// and agent messages in chronological sequence).
		// We keep non-live-chat messages (AI conversation) in their original order,
		// then append the existing live chat messages followed by newly backfilled ones.
		const existingLiveChatMessages = existingStoreChatItems.filter(isStoreTwilioLiveChatMessage);
		const nonLiveChatMessages = existingStoreChatItems.filter(
			(m) => !isStoreTwilioLiveChatMessage(m),
		);
		const mergedMessages = [
			...nonLiveChatMessages,
			...existingLiveChatMessages,
			...backfilledStoreChatItems,
		];

		setState({
			conversations: {
				...conversations,
				[conversationId]: {
					...currentConversation,
					messages: mergedMessages,
				},
			},
		});
	};

export type HydrateTwilioConversationParams = {
	conversationId: string;
	conversationSid: string;
	ocgTwilioClientModule: OcgTwilioClientModule;
};

/**
 * Hydrates a single Twilio conversation into `twilioConversationsWithMetadata`
 * by obtaining a Twilio client and fetching the Conversation object.
 * Unlike `initializeTwilioConversationImpl`, this action does NOT trigger
 * live chat initialization or any side effects beyond populating the store.
 */
export const hydrateTwilioConversationImpl =
	({
		conversationId,
		conversationSid,
		ocgTwilioClientModule,
	}: HydrateTwilioConversationParams): ChatStoreAction =>
	async ({ getState, setState }, containerProps) => {
		const { ocgClient, ufoExperiences } = containerProps;
		const { getTwilioConversation } = ocgTwilioClientModule;

		// Derive the help-kosh customer experience ID from the help-center ARI on
		// `containerProps`. It is required by every downstream layer; if it cannot
		// be resolved (no `customerHubId`, or the ARI doesn't contain a
		// help-center segment) we throw at the contract boundary so the failure
		// is surfaced via `containerProps.setGlobalError` through the standard
		// `createSafeAction` error path (BEARS-1780).
		const helpCenterId = containerProps.customerHubId
			? extractHelpCenterIdFromAri(containerProps.customerHubId)
			: null;
		if (!helpCenterId) {
			throw new Error('helpCenterId must be provided for Twilio');
		}

		const { attachTokenRefresh } = await import(
			/* webpackChunkName: "@atlassian/conversation-assistant-widget/ocg-token-refresh" */ '../../extensions/twilio/ocg-token-refresh'
		);

		const twilioClientContext: TwilioClientContext = {
			getState,
			setState,
			ocgTwilioClientModule,
			attachTokenRefresh,
			ocgClient,
			helpCenterId,
			ufoExperiences,
		};

		// FE observability (BEARS-1036): "Continue active chat" success event for
		// the initial chat-history rehydration path on widget mount.
		const ufoExperience = startLiveChatUfoExperience(
			ufoExperiences,
			'HYDRATE_TWILIO_LIVE_CHAT',
			conversationId,
		);

		try {
			const client = await getOrCreateTwilioClient(twilioClientContext);
			const conversation = await getTwilioConversation(client, conversationSid);
			// FE observability (BEARS-1036): success once the Twilio conversation
			// has been resolved successfully.
			ufoExperience?.success();

			setState({
				twilioConversationsWithMetadata: {
					...getState().twilioConversationsWithMetadata,
					[conversationId]: {
						conversation,
						conversationSid,
						unreadMessagesCount: 0,
						isUnreadMessagesCountSet: false,
					},
				},
			});
		} catch (error) {
			// FE observability (BEARS-1036): mark failure on any error path,
			// including the expected `Not Found` case where the Twilio resource
			// has been cleaned up. Tracking these as failures gives us a signal
			// when the rate of "lost" conversations spikes.
			ufoExperience?.failure();
			const errorMessage = error instanceof Error ? error.message : String(error);

			if (errorMessage === 'Not Found') {
				markTwilioConversationEndedWhenNotFound({ conversationId, getState, setState });
				return;
			}

			logException({
				exception: error,
				name: 'TwilioConversationHydrateFailed',
			});
		}
	};

export type UpdateTwilioUnreadMessagesCountParams = {
	conversationId: string;
	unreadMessagesCount: number;
};

export const updateTwilioUnreadMessagesCountImpl =
	({
		conversationId,
		unreadMessagesCount,
	}: UpdateTwilioUnreadMessagesCountParams): ChatStoreAction =>
	({ getState, setState }) => {
		const currentMetadata = getState().twilioConversationsWithMetadata;
		const existing = currentMetadata[conversationId];
		if (!existing) {
			return;
		}
		setState({
			twilioConversationsWithMetadata: {
				...currentMetadata,
				[conversationId]: {
					...existing,
					unreadMessagesCount,
					isUnreadMessagesCountSet: true,
				},
			},
		});
	};

export type CheckInitialUnreadMessagesParams = {
	conversationId: string;
	conversation: TwilioConversation;
	helpSeekerIdentity: string | null;
};

/**
 * Fetches recent messages from a Twilio Conversation to find the latest agent
 * message and compute the initial unread count. Reads the latest store state
 * via `getState()` so the race-condition guard against `isUnreadMessagesCountSet`
 * is never stale.
 */
export const checkInitialUnreadMessagesImpl =
	({
		conversationId,
		conversation,
		helpSeekerIdentity,
	}: CheckInitialUnreadMessagesParams): ChatStoreAction =>
	async ({ getState, setState }) => {
		try {
			const paginator = await conversation.getMessages();

			// Guard against race condition with the `messageAdded` listener:
			// if `updateTwilioUnreadMessagesCount` was already called (e.g. a
			// real-time message arrived first), skip this initial computation.
			// Reading from `getState()` ensures we see the latest store state.
			const { twilioConversationsWithMetadata } = getState();
			if (twilioConversationsWithMetadata[conversationId]?.isUnreadMessagesCountSet) {
				return;
			}

			// Find the latest message authored by an agent (i.e. not the help
			// seeker) and use its index to compute the unread count.
			// paginator.items are in chronological order (oldest first), so we
			// search from the end to find the latest agent message.
			const latestAgentMessage = [...paginator.items]
				.reverse()
				.find((m) => m.author !== helpSeekerIdentity);

			if (latestAgentMessage) {
				const lastReadIndex = conversation.lastReadMessageIndex ?? -1;
				const unreadCount = Math.max(0, latestAgentMessage.index - lastReadIndex);

				setState({
					twilioConversationsWithMetadata: {
						...twilioConversationsWithMetadata,
						[conversationId]: {
							...twilioConversationsWithMetadata[conversationId],
							unreadMessagesCount: unreadCount,
							isUnreadMessagesCountSet: true,
						},
					},
				});
			}
		} catch (error) {
			logException({
				exception: error,
				name: 'TwilioInitialUnreadCheckFailed',
			});
		}
	};

export type TwilioAgentTypingParams = { identity: string };

export const addTwilioAgentTypingImpl =
	({ identity }: TwilioAgentTypingParams): ChatStoreAction =>
	({ setState, getState }) => {
		const current = getState().twilioAgentsTyping;
		if (!current.includes(identity)) {
			setState({ twilioAgentsTyping: [...current, identity] });
		}
	};

export const removeTwilioAgentTypingImpl =
	({ identity }: TwilioAgentTypingParams): ChatStoreAction =>
	({ setState, getState }) => {
		const current = getState().twilioAgentsTyping;
		if (current.includes(identity)) {
			setState({ twilioAgentsTyping: current.filter((id) => id !== identity) });
		}
	};

export const clearTwilioAgentsTypingImpl =
	(): ChatStoreAction =>
	({ setState }) => {
		setState({ twilioAgentsTyping: [] });
	};

export type SetTwilioAgentJoinedParams = { conversationId: string };

export const setTwilioAgentJoinedImpl =
	({ conversationId }: SetTwilioAgentJoinedParams): ChatStoreAction =>
	({ setState, getState }) => {
		const current = getState().twilioConversationAgentJoined;
		if (!current[conversationId]) {
			setState({
				twilioConversationAgentJoined: {
					...current,
					[conversationId]: true,
				},
			});
		}
	};
